// "Material to order" — the only job the old Procurement Log still had: material lines (WO + approved COs)
// that are not on a PO yet. Assign a vendor, flag "from stock", or create the PO — everything else lives on the PO rows.
import { useMemo, useState } from "react";
import { AlertTriangle, CheckSquare, ChevronDown, ChevronRight, Loader2, ShoppingCart, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { setProcurementItemFromStock, setProcurementItemVendor } from "@/lib/api";
import { VendorPicker } from "./VendorPicker";
import { BTN } from "@/components/common/btn";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pending = (i) => (i.material_cost || 0) > 0 && (i.po_status || "unordered") === "unordered";

export function MaterialToOrder({ woId, bomData, manufacturers, onCreatePOForItem, onCreatePOForManufacturer, onChanged }) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(null);
  const items = useMemo(() => (bomData?.items || []).filter(pending), [bomData]);
  const groups = useMemo(() => {
    const by = {};
    items.forEach(i => { (by[i.manufacturer || ""] = by[i.manufacturer || ""] || []).push(i); });
    return Object.entries(by).sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  }, [items]);
  if (!items.length) return null;
  const toOrder = items.filter(i => !i.from_stock);
  const total = toOrder.reduce((s, i) => s + (i.material_cost || 0) * (i.qty || 1), 0);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="border border-amber-200 rounded bg-amber-50/40" data-testid="material-to-order">
      <button type="button" className="flex items-center gap-2 w-full px-3 py-1.5 text-left" onClick={() => setOpen(o => !o)} data-testid="material-to-order-toggle">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-amber-600" /> : <ChevronRight className="w-3.5 h-3.5 text-amber-600" />}
        <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
        <span className="text-xs font-semibold text-amber-900 flex-1">Material not yet on a PO</span>
        <span className="text-[11px] text-amber-800" data-testid="material-to-order-summary">{toOrder.length} line{toOrder.length === 1 ? "" : "s"} · {fmt(total)}{items.length - toOrder.length ? ` · ${items.length - toOrder.length} from stock` : ""}</span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1.5">
          {groups.map(([vendor, rows]) => {
            const orderable = rows.filter(i => !i.from_stock);
            return (
              <div key={vendor || "__none"} className="bg-white border rounded" data-testid={`material-group-${vendor || "no-vendor"}`}>
                <div className="flex items-center gap-2 px-2 py-1 border-b bg-slate-50">
                  <span className={`text-xs font-medium flex-1 ${vendor ? "text-slate-800" : "text-slate-500 italic"}`}>{vendor || "No vendor assigned"}</span>
                  <span className="text-[11px] text-slate-500">{rows.length} line{rows.length === 1 ? "" : "s"}</span>
                  {vendor && orderable.length > 0 && (
                    <Button type="button" size="sm" className={`${BTN.write} h-6 text-[10px] px-2`} disabled={busy === vendor}
                      onClick={() => run(vendor, () => onCreatePOForManufacturer(vendor, orderable))} data-testid={`material-create-po-${vendor}`}>
                      {busy === vendor ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />}<span className="ml-1">Create PO ({orderable.length})</span>
                    </Button>
                  )}
                </div>
                {rows.map(item => {
                  const qty = item.qty || 1;
                  const onHand = item.on_hand ?? 0;
                  return (
                    <div key={item.id} className={`grid grid-cols-[minmax(0,2fr)_90px_minmax(0,1.2fr)_minmax(150px,1.2fr)_44px_70px_80px_32px] gap-1 items-center px-2 py-1 text-xs border-t border-slate-100 ${item.from_stock ? "opacity-60" : ""}`} data-testid={`material-item-${item.id}`}>
                      <span className="font-medium text-slate-700 truncate">{item.name || "Unnamed"}</span>
                      <span className="text-slate-500 font-mono truncate text-[11px]">{item.part_number || "-"}</span>
                      <span className="text-slate-500 truncate">{item.scope_name || (item.source === "Change Order" ? item.source_name : "")}</span>
                      <div>{!vendor && <VendorPicker manufacturers={manufacturers} saving={busy === `v:${item.id}`} testIdSuffix={item.id}
                        onSelect={(name) => run(`v:${item.id}`, () => setProcurementItemVendor(woId, item.id, name), `Vendor set to ${name}`)} />}</div>
                      <span className="text-center tabular-nums">{qty}</span>
                      <button type="button" onClick={() => run(`s:${item.id}`, () => setProcurementItemFromStock(woId, item.id, !item.from_stock))}
                        className={`flex items-center justify-center gap-1 ${item.from_stock ? "text-emerald-600" : "text-slate-400"} hover:text-emerald-700`}
                        title={item.from_stock ? "Fulfilling from stock — click to order instead" : `On hand ${onHand} of ${qty} — click to pull from stock`} data-testid={`material-stock-toggle-${item.id}`}>
                        {item.from_stock ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}<span className="text-[10px]">{item.from_stock ? "Stock" : onHand}</span>
                      </button>
                      <span className="text-right tabular-nums font-medium">{fmt((item.material_cost || 0) * qty)}</span>
                      <div className="flex justify-center">
                        {vendor && !item.from_stock && (
                          <button type="button" disabled={busy === item.id} onClick={() => run(item.id, () => onCreatePOForItem(item))} className="text-slate-400 hover:text-emerald-600" title="Create a PO for just this line" data-testid={`material-po-item-${item.id}`}>
                            {busy === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
